import FreightTablesApi from "../services/FreightTablesApi";
import ItinerariesApi from "@/modules/Itineraries/services/ItinerariesApi";
import { ITINERARY_STATUS } from "@/modules/Itineraries/constants/itineraryStatus";
import {
  mapFreightTableFromApi,
  mapFreightTableDetailsFromLegacyApi,
  mapFreightTableFromLegacyApi,
  mapCarrierFromLegacyApi,
  getLegacyCarriers,
  mapRouteFromLegacyApi,
  mapValidityFromLegacyApi,
  mapValidityDetailsFromLegacyApi,
  getLegacyRoutes,
  getLegacyValidities,
  getLegacyCargos,
  mapCargoFromLegacyApi,
  getLegacyTransports,
  mapTransportFromLegacyApi,
  mapIdentificationToApi,
  mapSupplierOptionsFromApi,
  mapSuppliersToApi,
  mapActiveCarriersToSuppliers,
  mapLinkedCarrierIds,
  mapCarriersToApi,
  mapHeaderToConfigRoutesForm,
  mapNewRoutesToApi,
  mapHistoryFromApi,
  mapCarrierNamesFromRaw,
  mapDraftFromApi,
  mapDomainOptionsFromApi,
  mapConfigRoutesToApi,
} from "../utils/freightTableMapper";
import { sortValiditiesByStartDesc } from "../utils/validityOrder";
import { mapNewValidityContextFromRoute } from "../utils/newValidityForm";
import {
  FREIGHT_TABLE_STATUS,
  FREIGHT_TABLE_STATUS_FILTER,
} from "../constants/freightTableStatus";
import {
  FREIGHT_VALIDITY_STATUS,
  FREIGHT_VALIDITY_STATUS_FILTER,
} from "../constants/freightValidityStatus";
import { SUPPLIER_SEARCH_MIN_LENGTH } from "../constants/freightTableOptions";

// `search` e nao `cd`/`descr`: esses dois sao o filtro exato da listagem antiga, que
// continua exata. A busca parcial desta tela vive no parametro proprio, e quem decide
// entre codigo e descricao passou a ser o backend.
function buildSearchParams(search) {
  const value = String(search || "").trim();
  if (!value) return {};

  return { search: value };
}

function toApiDate(isoDate) {
  const parts = String(isoDate || "").split("-");
  if (parts.length !== 3) return isoDate;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

const DIA_DIGITADO = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

function pad2(value) {
  return `0${value}`.slice(-2);
}

/** dd/mm/aaaa digitado -> aaaa-mm-dd. "" quando não é uma data completa. */
function toIsoDay(term) {
  const match = DIA_DIGITADO.exec(String(term || "").trim());
  if (!match) return "";
  return `${match[3]}-${pad2(match[2])}-${pad2(match[1])}`;
}

/** Corta a hora: a API devolve "2024-06-01 00:00:00" ou o ISO com T. */
function isoDay(value) {
  return String(value || "").slice(0, 10);
}

// Data completa digitada busca a vigência que **contém** o dia — casar só com a
// data de início ou de fim deixava invisível a vigência que o operador está
// olhando no calendário. Termo parcial ("06/2024") segue casando por texto.
function filterByValidityDate(rows, term) {
  if (!term) return rows;

  const dia = toIsoDay(term);
  if (!dia) {
    return rows.filter(
      (row) => row.inicio.indexOf(term) !== -1 || row.fim.indexOf(term) !== -1,
    );
  }

  return rows.filter((row) => {
    const inicio = isoDay(row.inicioIso);
    if (!inicio || inicio > dia) return false;

    const fim = isoDay(row.fimIso);
    return !fim || fim >= dia;
  });
}

function filterByStatus(rows, status) {
  if (!status) return rows;
  return rows.filter((row) => row.statusValor === Number(status));
}

function sortByText(rows, sort, getText) {
  if (!sort) return rows;

  const direction = sort === "za" ? -1 : 1;
  return rows.slice().sort(
    (a, b) =>
      direction *
      String(getText(a)).localeCompare(String(getText(b)), "pt-BR", {
        sensitivity: "base",
      }),
  );
}

function buildFilterParams(filters) {
  const params = { ...buildSearchParams(filters.search) };

  if (
    filters.tableStatus &&
    filters.tableStatus !== FREIGHT_TABLE_STATUS_FILTER.ALL
  ) {
    params.table_status = filters.tableStatus;
  }

  if (
    filters.effectivePeriodStatus &&
    filters.effectivePeriodStatus !== FREIGHT_VALIDITY_STATUS_FILTER.ALL
  ) {
    params.effective_period_status = filters.effectivePeriodStatus;
  }

  if (filters.isDraft !== null && filters.isDraft !== undefined) {
    params.isDraft = filters.isDraft;
  }

  const suppliers = (filters.fornecedor || []).map(Number).filter(Boolean);
  if (suppliers.length) {
    params.fornecedor = suppliers;
  }

  // vigenciaInicio/vigenciaFim e nao dtInicio/dtFim: esses dois sao o filtro da listagem
  // antiga, que pergunta "comeca a partir de" / "termina ate". Esta tela pergunta outra
  // coisa — "vigente no periodo" — e por isso tem parametro proprio.
  if (filters.dtInicio) {
    params.vigenciaInicio = toApiDate(filters.dtInicio);
  }

  if (filters.dtFim) {
    params.vigenciaFim = toApiDate(filters.dtFim);
  }

  return params;
}

// Descarta respostas obsoletas quando o usuário dispara buscas em sequência
// (mesmo guard do fetchRoteiros).
let requestSeq = 0;

export async function fetchFreightTables({ commit, state }, overrides = {}) {
  const seq = ++requestSeq;
  commit("SET_LOADING", true);
  commit("SET_ERROR", null);

  const filters = {
    search:
      overrides.search !== undefined ? overrides.search : state.filters.search,
    sort: overrides.sort !== undefined ? overrides.sort : state.filters.sort,
    tableStatus:
      overrides.tableStatus !== undefined
        ? overrides.tableStatus
        : state.filters.tableStatus,
    effectivePeriodStatus:
      overrides.effectivePeriodStatus !== undefined
        ? overrides.effectivePeriodStatus
        : state.filters.effectivePeriodStatus,
    isDraft:
      overrides.isDraft !== undefined
        ? overrides.isDraft
        : state.filters.isDraft,
    fornecedor:
      overrides.fornecedor !== undefined
        ? overrides.fornecedor
        : state.filters.fornecedor,
    dtInicio:
      overrides.dtInicio !== undefined
        ? overrides.dtInicio
        : state.filters.dtInicio,
    dtFim:
      overrides.dtFim !== undefined ? overrides.dtFim : state.filters.dtFim,
  };
  commit("SET_FILTERS", filters);

  const params = {
    ...buildFilterParams(filters),
    page: overrides.page || 1,
    per_page: overrides.perPage || state.pagination.perPage,
  };

  if (filters.sort) {
    params.sort = filters.sort;
  }

  try {
    const response = await FreightTablesApi.fetchList(params);
    if (seq !== requestSeq) return;

    const body = response.data;
    const meta = body.meta || {};

    // Não há mais SET_RAW_ITEMS aqui: a listagem não devolve a árvore de rotas.
    // Quem precisa dela é o Detalhes, via resolveRawItem.
    commit("SET_FREIGHT_TABLES", {
      items: (body.data || []).map(mapFreightTableFromApi),
      pagination: {
        current: meta.current_page,
        perPage: Number(meta.per_page),
        total: meta.total,
        lastPage: meta.last_page,
      },
    });

    // Contadores das abas vêm no mesmo request.
    if (meta.contadores_por_aba) {
      commit("SET_COUNTS", meta.contadores_por_aba);
    }
  } catch (error) {
    if (seq !== requestSeq) return;
    commit("SET_ERROR", error);
    throw error;
  } finally {
    if (seq === requestSeq) commit("SET_LOADING", false);
  }
}

export async function approveTablesValidities(
  { dispatch, state },
  { freightTableIds },
) {
  const response = await FreightTablesApi.approveTablesValidities(
    freightTableIds,
  );

  await dispatch("fetchFreightTables", { page: state.pagination.current });

  return response.data;
}

export async function cancelTablesValidities(
  { dispatch, state },
  { freightTableIds },
) {
  const response = await FreightTablesApi.cancelTablesValidities(
    freightTableIds,
  );

  await dispatch("fetchFreightTables", { page: state.pagination.current });

  return response.data;
}

export async function searchSuppliers(context, { search }) {
  const term = String(search || "").trim();
  if (term.length < SUPPLIER_SEARCH_MIN_LENGTH) return [];

  const response = await FreightTablesApi.searchSuppliers(term);

  return mapSupplierOptionsFromApi(response.data);
}

export async function createFreightTableDraft(
  context,
  { identification, header },
) {
  const payload = mapIdentificationToApi(identification, header);
  const response = await FreightTablesApi.storeFirst(payload);
  return { tabfreId: response.data.tabfre_id };
}

export async function updateFreightTableDraft(
  context,
  { identification, header, tabfreId },
) {
  const payload = mapIdentificationToApi(identification, header);
  await FreightTablesApi.updateFirst(tabfreId, payload);
  return { tabfreId };
}

export async function fetchFreightTableDraft(context, { tabfreId }) {
  const response = await FreightTablesApi.fetchDraft(tabfreId);
  return mapDraftFromApi(response.data);
}

export async function finalizeFreightTableDraft(context, { tabfreId }) {
  await FreightTablesApi.finalizeDraft(tabfreId);
  context.commit("INVALIDATE_RAW_ITEM", tabfreId);
}

export async function deleteFreightTableDraft(context, { tabfreId }) {
  await FreightTablesApi.remove(tabfreId);
  context.commit("INVALIDATE_RAW_ITEM", tabfreId);
}

export async function storeFreightTableDraftSuppliers(
  context,
  { tabfreId, suppliers },
) {
  const payload = mapSuppliersToApi(tabfreId, suppliers);
  await FreightTablesApi.storeSuppliers(tabfreId, payload);
  context.commit("INVALIDATE_RAW_ITEM", tabfreId);
}

export async function updateFreightTableDraftSuppliers(
  context,
  { tabfreId, suppliers },
) {
  const payload = mapSuppliersToApi(tabfreId, suppliers);
  await FreightTablesApi.updateSuppliers(tabfreId, payload);
  context.commit("INVALIDATE_RAW_ITEM", tabfreId);
}

export async function fetchFreightTableOptions({ commit, state }) {
  // Listas de domínio não mudam durante a sessão da tela.
  if (state.options) return state.options;

  commit("SET_OPTIONS_LOADING", true);

  try {
    const response = await FreightTablesApi.fetchDomainOptions();
    const options = mapDomainOptionsFromApi(response.data);
    commit("SET_OPTIONS", options);

    return options;
  } finally {
    commit("SET_OPTIONS_LOADING", false);
  }
}

const ITINERARY_OPTIONS_PAGE_SIZE = 100;

export async function fetchItineraryOptions({ commit }) {
  commit("SET_ITINERARY_OPTIONS_LOADING", true);

  try {
    const rows = [];
    let page = 1;
    let lastPage = 1;

    // O endpoint limita per_page a 100 e o select precisa da lista inteira.
    do {
      const response = await ItinerariesApi.fetchAll({
        status: String(ITINERARY_STATUS.ATIVO),
        page,
        per_page: ITINERARY_OPTIONS_PAGE_SIZE,
      });
      const body = response.data || {};

      rows.push(...(body.data || []));
      lastPage = (body.meta && body.meta.last_page) || 1;
      page += 1;
    } while (page <= lastPage);

    const options = rows
      .filter(
        (raw) =>
          raw.isDraft === false &&
          Number(raw.status && raw.status.valor) === ITINERARY_STATUS.ATIVO,
      )
      .map((raw) => ({
        value: raw.itineraryId,
        label: raw.descr,
        cd: raw.cd,
        descr: raw.descr,
        // O header do card de rota mostra "<cd> • N paradas". A listagem já vem
        // com as paradas (ItineraryRepository faz eager load), não custa chamada.
        paradas: (raw.pessoasJuridicas || []).length,
      }));

    commit("SET_ITINERARY_OPTIONS", options);

    return options;
  } finally {
    commit("SET_ITINERARY_OPTIONS_LOADING", false);
  }
}

export async function saveFreightTableConfigRoutes(
  context,
  { tabfreId, form, identification, suppliers },
) {
  const payload = mapConfigRoutesToApi({
    form,
    tabfreId,
    identification,
    suppliers,
  });

  await FreightTablesApi.saveConfigRoutes(payload);
  context.commit("INVALIDATE_RAW_ITEM", tabfreId);
}

// Importação e download da planilha de rotas passam pelo store como todas as outras chamadas
// do módulo: a view não fala com o service direto.
export async function importFreightTableRoutes(context, { file }) {
  const response = await FreightTablesApi.importRoutesSpreadsheet(file);

  return response.data;
}

export async function downloadFreightTableRoutesTemplate() {
  const response = await FreightTablesApi.downloadRoutesSpreadsheet();

  return response.data;
}

export async function exportFreightTableCsv(
  context,
  { freightTableId, onlyActive },
) {
  const response = await FreightTablesApi.downloadRoutesSpreadsheet({
    tabfre_id: freightTableId,
    ativas: onlyActive ? 1 : 0,
  });

  return response.data;
}

async function loadRawItem(commit, id) {
  const response = await FreightTablesApi.fetchOne(id);
  const raw = response.data || null;
  if (!raw) return null;

  commit("SET_RAW_ITEMS", [raw]);

  return raw;
}

const pendingRawItems = {};

function resolveRawItem({ commit, state }, freightTableId) {
  const id = Number(freightTableId);
  if (state.rawById[id]) return Promise.resolve(state.rawById[id]);

  if (!pendingRawItems[id]) {
    pendingRawItems[id] = loadRawItem(commit, id).finally(() => {
      delete pendingRawItems[id];
    });
  }

  return pendingRawItems[id];
}

// Entrar numa tela da tabela (Detalhes, Adicionar rota, Adicionar transportadora) relê do
// servidor; o cache serve só às abas e painéis da mesma visita. Quem grava sem passar por este
// store — importação e aprovação em lote na listagem, tela legada, outro usuário — não o
// invalida, e a tela mostraria a tabela como estava na visita anterior.
function reloadRawItem(context, freightTableId) {
  context.commit("INVALIDATE_RAW_ITEM", freightTableId);

  return resolveRawItem(context, freightTableId);
}

export async function fetchFreightTableDetails(context, { freightTableId }) {
  const { commit } = context;
  commit("SET_DETAILS_LOADING", true);

  try {
    const raw = await reloadRawItem(context, freightTableId);
    if (!raw) throw new Error("Tabela de frete não encontrada");

    commit("SET_DETAILS", mapFreightTableDetailsFromLegacyApi(raw));
  } catch (error) {
    commit("SET_ERROR", error);
    throw error;
  } finally {
    commit("SET_DETAILS_LOADING", false);
  }
}

let carriersSeq = 0;

export async function fetchCarriers(context, { freightTableId, ...overrides }) {
  const { commit, state } = context;
  const seq = ++carriersSeq;
  commit("SET_CARRIERS_LOADING", true);

  const filters = {
    search:
      overrides.search !== undefined
        ? overrides.search
        : state.carriersFilters.search,
    sort:
      overrides.sort !== undefined
        ? overrides.sort
        : state.carriersFilters.sort,
    status:
      overrides.status !== undefined
        ? overrides.status
        : state.carriersFilters.status,
  };
  commit("SET_CARRIERS_FILTERS", filters);

  const page = overrides.page || 1;
  const perPage = overrides.perPage || state.carriersPagination.perPage;

  try {
    const raw = await resolveRawItem(context, freightTableId);
    if (seq !== carriersSeq) return;
    if (!raw) throw new Error("Tabela de frete não encontrada");

    let rows = getLegacyCarriers(raw).map(mapCarrierFromLegacyApi);

    const search = String(filters.search || "")
      .trim()
      .toLowerCase();
    if (search) {
      rows = rows.filter(
        (row) => String(row.nome).toLowerCase().indexOf(search) !== -1,
      );
    }

    rows = filterByStatus(rows, filters.status);
    rows = sortByText(rows, filters.sort, (row) => row.nome);

    const start = (page - 1) * perPage;

    commit("SET_CARRIERS", {
      items: rows.slice(start, start + perPage),
      pagination: {
        current: page,
        perPage,
        total: rows.length,
        lastPage: Math.max(1, Math.ceil(rows.length / perPage)),
      },
    });
  } catch (error) {
    if (seq !== carriersSeq) return;
    commit("SET_ERROR", error);
    throw error;
  } finally {
    if (seq === carriersSeq) commit("SET_CARRIERS_LOADING", false);
  }
}

// Relê a tabela depois de uma escrita, para o Detalhes refletir o que foi gravado.
async function refreshRawItem(context, freightTableId) {
  const { commit, state } = context;
  const id = Number(freightTableId);

  try {
    return (await loadRawItem(commit, id)) || state.rawById[id] || null;
  } catch (error) {
    commit("INVALIDATE_RAW_ITEM", id);
    return null;
  }
}

let routesSeq = 0;

export async function fetchRoutes(
  context,
  { freightTableId, silent, ...overrides },
) {
  const { commit, state } = context;
  const seq = ++routesSeq;
  if (!silent) commit("SET_ROUTES_LOADING", true);

  const filters = {
    search:
      overrides.search !== undefined
        ? overrides.search
        : state.routesFilters.search,
    sort:
      overrides.sort !== undefined ? overrides.sort : state.routesFilters.sort,
    status:
      overrides.status !== undefined
        ? overrides.status
        : state.routesFilters.status,
  };
  commit("SET_ROUTES_FILTERS", filters);

  try {
    const raw = await resolveRawItem(context, freightTableId);
    if (seq !== routesSeq) return;
    if (!raw) throw new Error("Tabela de frete não encontrada");

    let rows = getLegacyRoutes(raw).map(mapRouteFromLegacyApi);

    const search = String(filters.search || "")
      .trim()
      .toLowerCase();
    if (search) {
      rows = rows.filter(
        (row) =>
          String(row.nome).toLowerCase().indexOf(search) !== -1 ||
          String(row.destino).toLowerCase().indexOf(search) !== -1,
      );
    }

    rows = filterByStatus(rows, filters.status);
    rows = sortByText(rows, filters.sort, (row) => row.destino || row.nome);

    commit("SET_ROUTES", rows);
  } catch (error) {
    if (seq !== routesSeq) return;
    commit("SET_ERROR", error);
    throw error;
  } finally {
    if (seq === routesSeq) commit("SET_ROUTES_LOADING", false);
  }
}

export async function fetchRouteCargos(context, { freightTableId, routeId }) {
  const raw = await resolveRawItem(context, freightTableId);
  if (!raw) throw new Error("Tabela de frete não encontrada");

  const route = getLegacyRoutes(raw).filter(
    (item) => Number(item.tabfrerot_id) === Number(routeId),
  )[0];

  const rows = getLegacyCargos(route).map(mapCargoFromLegacyApi);

  return { items: rows, total: rows.length };
}

export async function fetchRouteTransports(
  context,
  { freightTableId, routeId },
) {
  const raw = await resolveRawItem(context, freightTableId);
  if (!raw) throw new Error("Tabela de frete não encontrada");

  const route = getLegacyRoutes(raw).filter(
    (item) => Number(item.tabfrerot_id) === Number(routeId),
  )[0];

  const rows = getLegacyTransports(route).map(mapTransportFromLegacyApi);

  return { items: rows, total: rows.length };
}

export async function fetchRouteValidities(
  context,
  { freightTableId, routeId, status, search },
) {
  const raw = await resolveRawItem(context, freightTableId);
  if (!raw) throw new Error("Tabela de frete não encontrada");

  const route = getLegacyRoutes(raw).filter(
    (item) => Number(item.tabfrerot_id) === Number(routeId),
  )[0];

  let rows = sortValiditiesByStartDesc(
    getLegacyValidities(route).map(mapValidityFromLegacyApi),
  );

  rows = filterByValidityDate(rows, String(search || "").trim());

  const countByStatus = (valor) =>
    rows.filter((row) => row.statusValor === valor).length;
  const counts = {
    [FREIGHT_VALIDITY_STATUS.PENDENTE]: countByStatus(
      FREIGHT_VALIDITY_STATUS.PENDENTE,
    ),
    [FREIGHT_VALIDITY_STATUS.APROVADA]: countByStatus(
      FREIGHT_VALIDITY_STATUS.APROVADA,
    ),
    [FREIGHT_VALIDITY_STATUS.CANCELADA]: countByStatus(
      FREIGHT_VALIDITY_STATUS.CANCELADA,
    ),
  };

  if (status) {
    rows = rows.filter((row) => row.statusValor === Number(status));
  }

  return { items: rows, total: rows.length, counts };
}

export async function fetchValidityDetails(
  context,
  { freightTableId, routeId, validityId },
) {
  const raw = await resolveRawItem(context, freightTableId);
  if (!raw) throw new Error("Tabela de frete não encontrada");

  const route = getLegacyRoutes(raw).filter(
    (item) => Number(item.tabfrerot_id) === Number(routeId),
  )[0];
  const validity = getLegacyValidities(route).filter(
    (item) => Number(item.tabfrerotvig_id) === Number(validityId),
  )[0];
  if (!validity) throw new Error("Vigência não encontrada");

  return mapValidityDetailsFromLegacyApi(validity);
}

export async function updateValidityStatus(
  context,
  { freightTableId, validityIds, status },
) {
  const response =
    Number(status) === FREIGHT_VALIDITY_STATUS.APROVADA
      ? await FreightTablesApi.approveValidities(validityIds)
      : await FreightTablesApi.cancelValidities(validityIds);

  await refreshRawItem(context, freightTableId);

  return response.data;
}

export async function fetchNewValidityContext(
  context,
  { freightTableId, routeId },
) {
  const raw = await resolveRawItem(context, freightTableId);
  if (!raw) throw new Error("Tabela de frete não encontrada");

  const route = getLegacyRoutes(raw).filter(
    (item) => Number(item.tabfrerot_id) === Number(routeId),
  )[0];

  return mapNewValidityContextFromRoute(route);
}

export async function createRouteValidity(
  context,
  { freightTableId, routeId, payload },
) {
  const response = await FreightTablesApi.storeRouteValidity(routeId, payload);

  await refreshRawItem(context, freightTableId);

  return response.data;
}

export async function fetchFreightTableHistory(
  context,
  { freightTableId, page, perPage, filters },
) {
  const { dispatch, state } = context;
  const params = { page: page || 1, per_page: perPage || 10 };
  const applied = filters || {};

  if (applied.search) params.search = applied.search;
  if (applied.events && applied.events.length) {
    params.event = applied.events.join(",");
  }
  if (applied.userId) params.user_id = applied.userId;
  if (applied.dateFrom) params.date_from = applied.dateFrom;
  if (applied.dateTo) params.date_to = applied.dateTo;

  const [response, raw] = await Promise.all([
    FreightTablesApi.fetchHistory(freightTableId, params),
    resolveRawItem(context, freightTableId).catch(() => null),
    dispatch("fetchFreightTableOptions").catch(() => null),
  ]);

  return {
    entries: mapHistoryFromApi(
      response.data.data,
      state.options,
      raw ? mapCarrierNamesFromRaw(raw) : {},
      raw ? { tpCalc: mapFreightTableFromLegacyApi(raw).calculoValor } : {},
    ),
    meta: response.data.meta,
  };
}

export async function fetchFreightTableRouteContext(
  context,
  { freightTableId },
) {
  const raw = await reloadRawItem(context, freightTableId);
  if (!raw) throw new Error("Tabela de frete não encontrada");

  return {
    codigo: raw.cd,
    descricao: raw.descr || "",
    isDraft: Number(raw.is_draft) === 1,
    routeCount: getLegacyRoutes(raw).length,
    header: mapHeaderToConfigRoutesForm(raw),
  };
}

export async function addFreightTableRoute(
  context,
  { freightTableId, route, header, descricao, numRotaOffset },
) {
  const payload = mapNewRoutesToApi({
    tabfreId: freightTableId,
    routes: [route],
    header,
    descricao,
    numRotaOffset,
  });
  const response = await FreightTablesApi.storeRoutes(freightTableId, payload);

  await refreshRawItem(context, freightTableId);

  return response.data;
}

export async function fetchFreightTableCarrierSuppliers(
  context,
  { freightTableId },
) {
  const raw = await reloadRawItem(context, freightTableId);
  if (!raw) throw new Error("Tabela de frete não encontrada");

  return {
    codigo: raw.cd,
    descricao: raw.descr || "",
    isDraft: Number(raw.is_draft) === 1,
    suppliers: mapActiveCarriersToSuppliers(raw),
    linkedIds: mapLinkedCarrierIds(raw),
  };
}

export async function updateFreightTableCarriers(
  context,
  { freightTableId, suppliers },
) {
  const payload = mapCarriersToApi(freightTableId, suppliers);
  const response = await FreightTablesApi.updateCarriers(
    freightTableId,
    payload,
  );

  await refreshRawItem(context, freightTableId);

  return response.data;
}

export async function updateCarriersStatus(
  { commit, dispatch, state },
  { freightTableId, carrierIds, active },
) {
  const status = active
    ? FREIGHT_TABLE_STATUS.ATIVA
    : FREIGHT_TABLE_STATUS.INATIVA;

  const response = await FreightTablesApi.updateCarriersStatus(
    carrierIds,
    status,
  );

  commit("SET_RAW_CARRIER_STATUS", { carrierIds, status });
  await dispatch("fetchCarriers", {
    freightTableId,
    page: state.carriersPagination.current,
  });

  return response.data;
}

export function updateCarrierStatus(
  { dispatch },
  { freightTableId, carrierId, active },
) {
  return dispatch("updateCarriersStatus", {
    freightTableId,
    carrierIds: [carrierId],
    active,
  });
}

export function resetListState({ commit }) {
  commit("RESET_LIST_STATE");
}

export function resetDetailsState({ commit }) {
  commit("RESET_DETAILS_STATE");
}
